import React, { useState } from 'react';
import { Droplet, Fan, Info, ChartLine, Snowflake, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from 'react-tooltip';
import { StatusCard } from '../Common/status-card';
import { ModeCard } from '../Common/mode-card';
import { Mode, Status } from '@/types';
import { IconTitle } from '@/components/ui/icon-title';

interface Unit {
  status: Status;
  type: Mode;
  count: number;
}

interface OperationInsightsProps {
  units?: Unit[];
  averageWorking?: number;
  avgToday?: number;
}

export function OperationInsights({
  averageWorking,
  avgToday,
  units,
}: OperationInsightsProps) {
  return (
    <div className="flex flex-col gap-[6px] p-2 bg-white rounded-[10px] shadow-card border border-light-grey h-full">
      <div className='flex flex-col gap-[4px] h-full'>
        {/* Header */}
        <IconTitle 
          icon={Activity} 
          title="Operation Insights"
        />

        {/* Average Working Section */}
        <div className="rounded-lg flex-1 flex flex-col">
          <div className="flex items-center gap-1">
            <span className="text-dark paragraph-11">Average Working %</span>
            <div
              data-tooltip-id="my-tooltip"
              data-tooltip-place="right"
              className="cursor-pointer text-gray-400 hover:text-gray-600 flex"
            >
              <Info className="inline-block h-[10px] w-[10px] ml-1 text-primary" >
              </Info>
            </div>
            <Tooltip arrowColor='bg-white' id="my-tooltip" opacity={1} style={{
              backgroundColor: 'white',
              padding: '4px 6px',
              gap: '8px',
              width: "165px",
              boxShadow: "-4px -4px 20px 0px #7090B03D",
              backdropFilter: "blur(20px)",
            }}>
              <p className='paragraph-9 text-dark-blue'>Working % indicates the AHU's operational level relative to its maximum capacity. Values are averaged across all active units.</p>
            </Tooltip>
          </div>
          <div className="flex flex-col flex-1">
            <div className="flex items-center gap-1">
              <span className="heading-2 text-black">{averageWorking !== null && averageWorking !== undefined ? averageWorking : "0"}</span>
              <span className="paragraph-16 text-black">%</span>
            </div>
            <div className="flex items-center gap-1 h-2">
              <span className="paragraph-9 text-dark-grey">Today Avg.</span>
              <span className="paragraph-9 text-gray-500">{avgToday !== null && avgToday !== undefined ? avgToday : "0"} %</span>
            </div>
          </div>
        </div>
      </div>

        {/* Cards Container */}
        <div className="flex flex-col gap-[4px] mt-auto">
          {/* Status Cards */}
          <div className="flex gap-[4px] min-h-[2.75rem]">
            <StatusCard
              type="alarm"
              count={units?.find(v => v.status == "alarm")?.count || 0}
              bgColor="bg-danger/20"
              dotColor="bg-dark-orange"
              textColor="text-danger"
            />
            <StatusCard
              type="on"
              count={units?.find(v => v.status == "on")?.count || 0}
              bgColor="bg-[#DCEDDD]"
              dotColor="bg-[#15B8B4]"
              textColor="text-dark-green"
            />
            <StatusCard
              type="off"
              count={units?.find(v => v.status == "off")?.count || 0}
              bgColor="bg-gray-200/40"
              dotColor="bg-[#B4B4B4]"
              textColor="text-dark-grey"
            />
          </div>

          {/* Mode Cards */}
          <div className="flex gap-[4px] min-h-[2.75rem]">
            <ModeCard type="cool" count={units?.find(v => v.type == "cool")?.count || 0} />
            <ModeCard type="fan" count={units?.find(v => v.type == "fan")?.count || 0} />
            <ModeCard type="dry" count={units?.find(v => v.type == "dry")?.count || 0} />
          </div>
        </div>
    </div>
  );
}
